import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { Skeleton } from "@shared-ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { DataTable } from "@/features/catalog/components/data-table";
import {
	type AdminBrandRow,
	listAdminBrandRows,
} from "@/features/catalog/services/catalog-api.service";
import { hasPermission } from "@/lib/permissions";
import { useDebounce } from "@/lib/use-debounce";

/**
 * The brand list.
 *
 * Shows WITHDRAWN and SUPERSEDED brands, unlike the customer-facing list which
 * hides both. An operator needs to see them: hiding a superseded brand makes a
 * merge look like a deletion, and hiding a withdrawn one hides the thing they
 * came to restore.
 *
 * The two are rendered differently on purpose. They are not two shades of
 * "inactive" -- withdrawal is a business decision that its products are off
 * sale, supersession is a record-keeping artefact of a merge. One word for both
 * is what the schema spent a migration separating.
 */

const ALL = "all";

/** How a brand's state reads, and why each is toned as it is. */
function stateBadge(brand: AdminBrandRow) {
	if (brand.mergedIntoId) {
		// Neutral: nothing is wrong. The duplicate was resolved, which is the
		// tool working.
		return <Badge variant="default">Merged away</Badge>;
	}
	if (!brand.isActive) {
		// A warning, not a neutral: a withdrawn brand's products are out of every
		// customer's reach, which is a state someone should be able to see at a
		// glance rather than infer from a missing tick.
		return <Badge variant="warning">Withdrawn</Badge>;
	}
	return <Badge variant="success">Live</Badge>;
}

export function BrandsDashboardPage() {
	const navigate = useNavigate();
	const operator = useAdminAuthStore((state) => state.operator);
	const canEdit = hasPermission(operator, "admin:catalog:brands");

	const [query, setQuery] = useState("");
	const [state, setState] = useState<string>(ALL);
	const [page, setPage] = useState(1);
	const debouncedQuery = useDebounce(query, 300);

	const { data, isPending, isError, isFetching, refetch } = useQuery({
		queryKey: ["admin-brands", debouncedQuery, state, page],
		queryFn: () =>
			listAdminBrandRows({
				q: debouncedQuery || undefined,
				// `undefined` for "all", not `false`: the filter is tri-state and an
				// explicit `false` would ask for withdrawn brands only.
				isActive: state === ALL ? undefined : state === "live",
				page,
			}),
		placeholderData: (previous) => previous,
	});

	const brands = data?.items ?? [];
	const totalPages = data?.totalPages ?? 1;
	// A refetch with a page already on screen, as opposed to the first load.
	const isRefreshing = isFetching && !isPending;

	const onFilterChange =
		(setter: (value: string) => void) => (value: string) => {
			setter(value);
			// Back to page 1: a filter that narrows the set while the reader sits on
			// page 6 shows an empty table and no explanation.
			setPage(1);
		};

	return (
		<div className="grid gap-4">
			<Card>
				<CardHeader className="flex flex-row items-start justify-between gap-4">
					<div>
						<CardTitle>Brands</CardTitle>
						<CardDescription>
							{data
								? `${data.total} brand${data.total === 1 ? "" : "s"}`
								: "Search by name, or filter by state."}
						</CardDescription>
					</div>
					{canEdit ? (
						<Button asChild size="sm">
							<Link to="/catalog/brands/new">New brand</Link>
						</Button>
					) : null}
				</CardHeader>
				<CardContent className="grid gap-4">
					<div className="grid gap-3 rounded-2xl border bg-slate-50/80 p-3 md:grid-cols-[minmax(0,1fr)_200px]">
						<Input
							placeholder="Search by name"
							value={query}
							onChange={(event) => {
								setQuery(event.target.value);
								setPage(1);
							}}
						/>
						<Select value={state} onValueChange={onFilterChange(setState)}>
							{/*
							 * `aria-label` because a Radix trigger takes its accessible name
							 * from the VALUE -- with "All brands" selected it would announce
							 * the value and nothing about what it controls.
							 */}
							<SelectTrigger aria-label="State">
								<SelectValue placeholder="State" />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									<SelectItem value={ALL}>All brands</SelectItem>
									<SelectItem value="live">Live</SelectItem>
									<SelectItem value="withdrawn">Withdrawn</SelectItem>
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>

					{isPending ? (
						<div className="grid gap-2">
							<Skeleton className="h-9 w-full" />
							<Skeleton className="h-9 w-full" />
							<Skeleton className="h-9 w-full" />
						</div>
					) : isError ? (
						<div className="grid gap-3 rounded-2xl border border-dashed p-6 text-center">
							<p className="text-sm text-muted-foreground">
								Could not load brands.
							</p>
							<div>
								<Button variant="outline" size="sm" onClick={() => refetch()}>
									Try again
								</Button>
							</div>
						</div>
					) : brands.length === 0 ? (
						<div className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
							No brands match these filters.
						</div>
					) : (
						<DataTable
							rows={brands}
							rowKey={(brand) => brand.id}
							isRefreshing={isRefreshing}
							onRowActivate={(brand) => navigate(`/catalog/brands/${brand.id}`)}
							rowLabel={(brand) => `Edit ${brand.name}`}
							columns={[
								{
									header: "Brand",
									cell: (brand) => (
										<div className="font-medium">{brand.name}</div>
									),
								},
								{
									header: "State",
									cell: stateBadge,
								},
								{
									header: "Products",
									align: "right",
									cellClassName: "tabular-nums",
									cell: (brand) => brand.productCount,
								},
							]}
						/>
					)}

					{totalPages > 1 ? (
						<div className="flex items-center justify-between border-t pt-3 text-sm">
							<span className="text-muted-foreground">
								Page {page} of {totalPages}
							</span>
							<div className="flex gap-2">
								<Button
									variant="outline"
									size="sm"
									disabled={page <= 1 || isRefreshing}
									onClick={() => setPage((value) => Math.max(1, value - 1))}
								>
									Previous
								</Button>
								{/*
								 * Guards read LOCAL page state, not the response's -- with
								 * `placeholderData` the response is the previous page while
								 * the next loads, so guarding on it lets a second click step
								 * past the last page. The clamp is deliberate redundancy for
								 * a guard someone later loosens.
								 */}
								<Button
									variant="outline"
									size="sm"
									disabled={page >= totalPages || isRefreshing}
									onClick={() =>
										setPage((value) => Math.min(totalPages, value + 1))
									}
								>
									Next
								</Button>
							</div>
						</div>
					) : null}
				</CardContent>
			</Card>
		</div>
	);
}
